import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Table,
  Badge,
  Stepper,
  TextInput,
  Select,
  NumberInput,
  Textarea,
  Skeleton,
  Progress,
  Card,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconSparkles,
  IconShoppingCartPlus,
  IconPlus,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import {
  useMenu,
  useAddDish,
  useParseMenuText,
  useMapIngredients,
  useCalculateQuantities,
  useGenerateMenuOrder,
  useAddToCart,
} from '@/hooks/api';

export default function MenuDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const menuId = Number(id) || 0;

  const { data: menu, isLoading } = useMenu(menuId);
  const addDish = useAddDish(menuId);
  const parseMenu = useParseMenuText(menuId);
  const mapIngredients = useMapIngredients(menuId);
  const calculateQty = useCalculateQuantities(menuId);
  const generateOrder = useGenerateMenuOrder(menuId);
  const addToCart = useAddToCart();

  const [activeStep, setActiveStep] = useState(0);

  // Add dish form
  const [dishName, setDishName] = useState('');
  const [dishCategory, setDishCategory] = useState<string | null>(null);
  const [dishPortions, setDishPortions] = useState<number>(1);

  // Parse form
  const [parseText, setParseText] = useState('');

  // Map results
  const [mapResult, setMapResult] = useState<{
    mapped: number;
    total: number;
    mappings: { ingredient_name: string; product_id: number | null; product_name: string | null; confidence: number }[];
  } | null>(null);

  const [addingToCart, setAddingToCart] = useState(false);

  const handleAddDish = async () => {
    if (!dishName.trim()) {
      notifications.show({ title: 'Missing Name', message: 'Please enter a dish name', color: 'orange' });
      return;
    }
    try {
      await addDish.mutateAsync({
        name: dishName.trim(),
        category: dishCategory || undefined,
        portions: dishPortions,
      });
      notifications.show({ title: 'Dish Added', message: `${dishName} added`, color: 'green' });
      setDishName('');
      setDishCategory(null);
      setDishPortions(1);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to add dish', color: 'red' });
    }
  };

  const handleParse = async () => {
    if (!parseText.trim()) {
      notifications.show({ title: 'No Text', message: 'Please enter menu text to parse', color: 'orange' });
      return;
    }
    try {
      await parseMenu.mutateAsync({ text: parseText });
      notifications.show({ title: 'Parsed', message: 'Menu text parsed successfully', color: 'green' });
      setParseText('');
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to parse menu text', color: 'red' });
    }
  };

  const handleMap = async () => {
    try {
      const res = await mapIngredients.mutateAsync();
      setMapResult(res.data as typeof mapResult);
      notifications.show({ title: 'Mapped', message: 'Ingredients mapped to products', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to map ingredients', color: 'red' });
    }
  };

  const handleCalculate = async () => {
    try {
      await calculateQty.mutateAsync();
      notifications.show({ title: 'Calculated', message: 'Quantities calculated', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to calculate quantities', color: 'red' });
    }
  };

  const handleGenerateOrder = async () => {
    try {
      const res = await generateOrder.mutateAsync();
      const orderData = res.data as { order_id?: number; order_number?: string } | undefined;
      notifications.show({
        title: 'Order Generated',
        message: orderData?.order_number
          ? `Order ${orderData.order_number} created`
          : 'Sales order created',
        color: 'green',
      });
      if (orderData?.order_id) {
        navigate(`/orders/${orderData.order_id}`);
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to generate order', color: 'red' });
    }
  };

  const handleAddScheduleToCart = async () => {
    if (!menu?.order_schedule?.length) return;
    setAddingToCart(true);
    let successCount = 0;
    try {
      for (const item of menu.order_schedule) {
        if (item.weekly_quantity > 0) {
          await addToCart.mutateAsync({
            product_id: item.product_id,
            quantity: item.weekly_quantity,
          });
          successCount++;
        }
      }
      if (successCount > 0) {
        notifications.show({
          title: 'Success',
          message: `Added ${successCount} item${successCount > 1 ? 's' : ''} to cart`,
          color: 'green',
        });
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to add items to cart', color: 'red' });
    } finally {
      setAddingToCart(false);
    }
  };

  const confidenceColor = (c: number) =>
    c >= 0.8 ? 'green' : c >= 0.5 ? 'yellow' : 'red';

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={300} />
        <Skeleton height={60} />
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (!menu) {
    return (
      <Stack gap="lg">
        <PageHeader title="Menu Not Found" />
        <Paper p="lg" radius="md" withBorder>
          <EmptyState
            title="Menu Not Found"
            description="This menu doesn't exist or you don't have access to it."
            action={
              <Button onClick={() => navigate('/menu-planner/menus')}>Back to Menus</Button>
            }
          />
        </Paper>
      </Stack>
    );
  }

  const hasDishes = menu.dishes && menu.dishes.length > 0;
  const hasSchedule = menu.order_schedule && menu.order_schedule.length > 0;
  const totalWeeklyCost = menu.order_schedule?.reduce((sum, i) => sum + i.weekly_cost, 0) ?? 0;

  return (
    <Stack gap="lg">
      <PageHeader
        title={menu.name}
        description={`${menu.cuisine_type || 'Menu'} \u2014 ${menu.cover_count} covers \u2014 ${menu.services_per_week}x/week`}
        actions={
          <Button variant="subtle" onClick={() => navigate('/menu-planner/menus')}>
            Back to Menus
          </Button>
        }
      />

      {/* Stepper */}
      <Paper p="lg" radius="md" withBorder>
        <Stepper
          active={activeStep}
          onStepClick={setActiveStep}
          allowNextStepsSelect={false}
        >
          {/* Step 1: Dishes */}
          <Stepper.Step label="Dishes" description="Add dishes to your menu">
            <Stack gap="md" mt="md">
              {/* Existing Dishes */}
              {hasDishes && (
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Dish</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Portions</Table.Th>
                      <Table.Th>Ingredients</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {menu.dishes.map((dish) => (
                      <Table.Tr key={dish.id}>
                        <Table.Td><Text fw={500} size="sm">{dish.name}</Text></Table.Td>
                        <Table.Td>
                          {dish.category && <Badge size="sm" variant="light">{dish.category}</Badge>}
                        </Table.Td>
                        <Table.Td>{dish.portions}</Table.Td>
                        <Table.Td>{dish.ingredients?.length || 0}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}

              {/* Add Dish Form */}
              <Paper p="md" radius="md" withBorder>
                <Stack gap="sm">
                  <Text fw={600} size="sm">Add a Dish</Text>
                  <Group grow>
                    <TextInput
                      placeholder="Dish name"
                      value={dishName}
                      onChange={(e) => setDishName(e.currentTarget.value)}
                    />
                    <Select
                      placeholder="Category"
                      data={['Appetizer', 'Entree', 'Side', 'Dessert', 'Beverage', 'Salad', 'Soup']}
                      value={dishCategory}
                      onChange={setDishCategory}
                      clearable
                    />
                    <NumberInput
                      placeholder="Portions"
                      value={dishPortions}
                      onChange={(v) => setDishPortions(Number(v) || 1)}
                      min={1}
                      style={{ maxWidth: 100 }}
                    />
                  </Group>
                  <Button
                    size="sm"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={handleAddDish}
                    loading={addDish.isPending}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Add Dish
                  </Button>
                </Stack>
              </Paper>

              {/* AI Parse */}
              <Paper p="md" radius="md" withBorder>
                <Stack gap="sm">
                  <Group gap="xs">
                    <IconSparkles size={16} />
                    <Text fw={600} size="sm">AI Parse Menu Text</Text>
                  </Group>
                  <Textarea
                    placeholder="Paste your full menu text here and AI will extract all dishes and ingredients..."
                    value={parseText}
                    onChange={(e) => setParseText(e.currentTarget.value)}
                    minRows={4}
                  />
                  <Button
                    size="sm"
                    leftSection={<IconSparkles size={14} />}
                    onClick={handleParse}
                    loading={parseMenu.isPending}
                    disabled={!parseText.trim()}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Parse Menu
                  </Button>
                </Stack>
              </Paper>

              <Group justify="flex-end">
                <Button onClick={() => setActiveStep(1)} disabled={!hasDishes}>
                  Next: Map Ingredients
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>

          {/* Step 2: Mapping */}
          <Stepper.Step label="Mapping" description="Map ingredients to products">
            <Stack gap="md" mt="md">
              <Button
                leftSection={<IconSparkles size={16} />}
                onClick={handleMap}
                loading={mapIngredients.isPending}
              >
                Map Ingredients to Products
              </Button>

              {mapResult && (
                <>
                  <Alert
                    color={mapResult.mapped === mapResult.total ? 'green' : 'yellow'}
                    icon={mapResult.mapped === mapResult.total ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />}
                    title={`${mapResult.mapped} of ${mapResult.total} ingredients mapped`}
                  />
                  <Table striped>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Ingredient</Table.Th>
                        <Table.Th>Matched Product</Table.Th>
                        <Table.Th>Confidence</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {mapResult.mappings.map((m, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td><Text size="sm">{m.ingredient_name}</Text></Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {m.product_name || <Text span c="dimmed">Not found</Text>}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Progress
                              value={m.confidence * 100}
                              color={confidenceColor(m.confidence)}
                              size="sm"
                              style={{ minWidth: 60 }}
                            />
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </>
              )}

              <Group justify="space-between">
                <Button variant="subtle" onClick={() => setActiveStep(0)}>Back</Button>
                <Button onClick={() => setActiveStep(2)}>
                  Next: Calculate Quantities
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>

          {/* Step 3: Quantities */}
          <Stepper.Step label="Quantities" description="Calculate weekly quantities">
            <Stack gap="md" mt="md">
              <Button
                leftSection={<IconSparkles size={16} />}
                onClick={handleCalculate}
                loading={calculateQty.isPending}
              >
                Calculate Quantities
              </Button>

              {hasSchedule && (
                <>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Product</Table.Th>
                        <Table.Th>Weekly Qty</Table.Th>
                        <Table.Th>Unit</Table.Th>
                        <Table.Th>Unit Price</Table.Th>
                        <Table.Th>Weekly Cost</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {menu.order_schedule.map((item) => (
                        <Table.Tr key={item.product_id}>
                          <Table.Td>
                            <div>
                              <Text fw={500} size="sm">{item.product_name}</Text>
                              {item.sku && <Text size="xs" c="dimmed">SKU: {item.sku}</Text>}
                            </div>
                          </Table.Td>
                          <Table.Td>{item.weekly_quantity}</Table.Td>
                          <Table.Td>{item.unit}</Table.Td>
                          <Table.Td>${item.unit_price.toFixed(2)}</Table.Td>
                          <Table.Td>
                            <Text fw={600} size="sm">${item.weekly_cost.toFixed(2)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>

                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" c="dimmed">Total Weekly Cost</Text>
                        <Text size="xl" fw={700}>${totalWeeklyCost.toFixed(2)}</Text>
                      </div>
                      <Group>
                        <Button
                          variant="light"
                          leftSection={<IconShoppingCartPlus size={16} />}
                          onClick={handleAddScheduleToCart}
                          loading={addingToCart}
                        >
                          Add to Cart
                        </Button>
                      </Group>
                    </Group>
                  </Card>
                </>
              )}

              <Group justify="space-between">
                <Button variant="subtle" onClick={() => setActiveStep(1)}>Back</Button>
                <Button onClick={() => setActiveStep(3)} disabled={!hasSchedule}>
                  Next: Generate Order
                </Button>
              </Group>
            </Stack>
          </Stepper.Step>

          {/* Step 4: Order */}
          <Stepper.Step label="Order" description="Generate sales order">
            <Stack gap="md" mt="md" align="center">
              <Text size="lg" ta="center">
                Ready to generate a sales order from this menu's weekly quantities?
              </Text>
              <Group>
                <Button
                  variant="light"
                  leftSection={<IconShoppingCartPlus size={16} />}
                  onClick={handleAddScheduleToCart}
                  loading={addingToCart}
                >
                  Add to Cart Instead
                </Button>
                <Button
                  leftSection={<IconCheck size={16} />}
                  onClick={handleGenerateOrder}
                  loading={generateOrder.isPending}
                >
                  Generate Sales Order
                </Button>
              </Group>
              <Button variant="subtle" onClick={() => setActiveStep(2)}>
                Back
              </Button>
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Paper>
    </Stack>
  );
}
